import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { DownloadSimple, Package, ShoppingCart, Clock } from '@phosphor-icons/react';

import { API_URL } from '../config';

const MyPurchasesPage = () => {
  const [purchases, setPurchases] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => { fetchPurchases(); }, []);

  const fetchPurchases = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/purchases`, { withCredentials: true });
      setPurchases(res.data);
    } catch (e) {
      console.error('Error fetching purchases:', e);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    try {
      return new Date(dateStr).toLocaleDateString('en-US', {
        year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit'
      });
    } catch { return dateStr; }
  };

  return (
    <div className="min-h-screen bg-[#0A0A0A] pt-20 pb-12">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="font-heading font-black text-3xl uppercase text-white mb-2" data-testid="purchases-title">
              My Purchases
            </h1>
            <p className="text-[#8892B0]">Your digital products and downloads</p>
          </div>
          <Link
            to="/store"
            className="flex items-center gap-2 px-5 py-2.5 border border-white/20 text-[#8892B0] font-bold uppercase tracking-wider text-sm hover:bg-white/5 transition-colors"
            data-testid="browse-store-btn"
          >
            <ShoppingCart size={16} /> Browse Store
          </Link>
        </div>

        {/* Purchases List */}
        {loading ? (
          <div className="text-center py-12 text-[#8892B0]">Loading purchases...</div>
        ) : purchases.length === 0 ? (
          <div className="text-center py-16 bg-[#131313] border border-white/5">
            <Package size={48} className="mx-auto text-[#5C6B82] mb-4" />
            <p className="text-[#8892B0] mb-2">No purchases yet</p>
            <p className="text-sm text-[#5C6B82] mb-6">Browse the store to find trading resources</p>
            <Link
              to="/store"
              className="inline-flex items-center gap-2 px-6 py-3 bg-[#007AFF] text-white font-bold uppercase tracking-widest hover:bg-[#005bb5] transition-colors"
              data-testid="go-to-store-btn"
            >
              <ShoppingCart size={16} /> Go to Store
            </Link>
          </div>
        ) : (
          <div className="space-y-4" data-testid="purchases-list">
            {purchases.map((purchase) => (
              <div
                key={purchase.purchase_id}
                className="bg-[#131313] border border-white/5 p-5 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4"
                data-testid={`purchase-${purchase.purchase_id}`}
              >
                <div className="flex items-start gap-4 flex-1 min-w-0">
                  {/* Product Image */}
                  <div className="w-16 h-16 bg-[#1A1A1A] flex-shrink-0 flex items-center justify-center border border-white/5 overflow-hidden">
                    {purchase.product?.image_url ? (
                      <img src={purchase.product.image_url} alt="" className="w-full h-full object-cover" />
                    ) : (
                      <Package size={24} className="text-[#5C6B82]" />
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <h3 className="text-white font-bold text-base truncate">{purchase.product_title}</h3>
                    <div className="flex items-center gap-3 mt-1">
                      <span className="text-[#007AFF] font-mono font-bold">${purchase.price.toFixed(2)}</span>
                      <span className="flex items-center gap-1 text-xs text-[#5C6B82]">
                        <Clock size={12} /> {formatDate(purchase.purchased_at)}
                      </span>
                    </div>
                  </div>
                </div>

                {/* Download Button */}
                {purchase.file_url ? (
                  <a
                    href={purchase.file_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-2 px-5 py-2.5 bg-[#34C759] text-black font-bold uppercase tracking-wider text-sm hover:bg-[#2db550] transition-colors flex-shrink-0"
                    data-testid={`download-${purchase.purchase_id}`}
                  >
                    <DownloadSimple size={16} weight="bold" /> Download
                  </a>
                ) : (
                  <span className="text-xs text-[#5C6B82] px-4 py-2 border border-white/10">
                    Check email for delivery
                  </span>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default MyPurchasesPage;
